import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import Stack from '@mui/material/Stack';
import InputLabel from '@mui/material/InputLabel';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
//import { createRole, updateRole, useCheckDuplicateRoleName, useGetRole } from 'api/role';
import { GetFormattedDate, GetFormattedTimeOnly } from 'utils/commonFunctions';
import { isEqual } from 'lodash-es';
import { Role } from 'types/userrole';
import { useInsertRoleRecord, useUpdateRoleRecord, useCheckDuplicateRoleName, useGetRoleModal } from 'api/userrole';
export interface RoleFormValues {
  userRoleID?: number;
  roleName: string;
  roleDescription: string;
  active?: boolean;
  updatedOn?: string;
}

const RoleForm = ({
  role,
  closeModal,
  onRoleCreated
}: {
  role: Role | null;
  closeModal: () => void;
  onRoleCreated?: (newRole: Role) => void;
}) => {
  const shouldFetch = Boolean(role?.userRoleID && open);

  const { data: latestRole } = useGetRoleModal(shouldFetch ? role?.userRoleID! : null);

  const timerRef = useRef<any>(null);
  const lastCheckedNameRef = useRef<string>('');
  const checkDuplicatePromiseRef = useRef<Promise<boolean> | null>(null);

  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);

  const initialValuesRef = useRef<any>(null);

  const checkDuplicate = useCallback(
    async (name: string, excludeID?: number) => {
      try {
        setDuplicateError(false);
        lastCheckedNameRef.current = name;

        const promise = useCheckDuplicateRoleName(name, excludeID);

        checkDuplicatePromiseRef.current = promise;

        const res = await promise;

        setIsDuplicate(res);

        return res;
      } catch (error) {
        setDuplicateError(true);
        lastCheckedNameRef.current = '';
        return false;
      } finally {
        checkDuplicatePromiseRef.current = null;
      }
    },
    []
  );

  const isClosingRef = useRef(false);
  const RoleSchema = Yup.object().shape({
    roleName: Yup.string()
      .trim()
      .required('Role name is required')
      .max(100, 'Name must be at most 100 characters')
      .transform((value) => (value ? value.trim() : '')),
    roleDescription: Yup.string()
      .trim()
      .max(250, 'Description must be at most 250 character')
      .transform((value) => (value ? value.trim() : '')),
    roleStatus: Yup.boolean().oneOf([true, false], 'Status is required')
  });


  const getInitialFormValues = (roleData: any): RoleFormValues => {
    if (roleData) {
      return {
        userRoleID: roleData.userRoleID,
        roleName: roleData.roleName || '',
        roleDescription: roleData.roleDescription || '',
        active: roleData.active ?? true,
        updatedOn: roleData.updatedOn
      };
    } else {
      return {
        roleName: '',
        roleDescription: '',
        active: true
      };
    }
  };
  const formik = useFormik<RoleFormValues>({
    initialValues: getInitialFormValues(latestRole),
    validationSchema: RoleSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const trimmedVal = values.roleName.trim();
      const originalVal = (role?.roleName || '').trim();

      if (timerRef.current) clearTimeout(timerRef.current);

      let duplicate = isDuplicate;

      if (checkDuplicatePromiseRef.current) {
        duplicate = await checkDuplicatePromiseRef.current;
      } else if (
        trimmedVal !== originalVal &&
        trimmedVal !== lastCheckedNameRef.current
      ) {
        duplicate = await checkDuplicate(
          trimmedVal,
          role?.userRoleID
        );
      }

      if (duplicate) {
        setSubmitting(false);
        return;
      }

      try {
        const cleanValues = RoleSchema.cast(values);
        const payload = {
          ...cleanValues,
          active: values.active
        };
        let res: any = null;

        if (role?.userRoleID) {
          res = await useUpdateRoleRecord(role.userRoleID, payload);
        } else {
          res = await useInsertRoleRecord(payload);
        }

        if (res?.primaryKeyID) {
          if (onRoleCreated && res) {
            const enriched: Role = {
              ...(res as any),
              userRoleID: res.primaryKeyID,
              roleName: values.roleName,
              updatedOn: res.updatedOn
            } as Role;
            onRoleCreated(enriched);
          }
        }
        resetForm();
        closeModal();
      } catch (error: any) {
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (latestRole && !initialValuesRef.current) {
      const initialValues = getInitialFormValues(latestRole);
      initialValuesRef.current = {
        roleName: initialValues.roleName,
        roleDescription: initialValues.roleDescription,
        active: initialValues.active
      };
    }
  }, [latestRole]);

  const hasFormChanges = useCallback(() => {
    if (!role) {
      return formik.values.roleName.trim().length > 0;
    }

    if (!initialValuesRef.current) return false;

    const currentValues = {
      roleName: formik.values.roleName,
      roleDescription: formik.values.roleDescription,
      active: formik.values.active
    };

    return !isEqual(currentValues, initialValuesRef.current);
  }, [formik.values, role]);



  const { errors, touched, getFieldProps, handleSubmit, setFieldValue, isSubmitting, values } = formik;

  useEffect(() => {
    const trimmedVal = values.roleName.trim();
    const originalVal = (role?.roleName || '').trim();

    if (!trimmedVal || trimmedVal === originalVal) {
      setIsDuplicate(false);
      return;
    }

    if (trimmedVal !== lastCheckedNameRef.current) {
      setIsDuplicate(false);
      setDuplicateError(false);
      lastCheckedNameRef.current = '';
    }

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      checkDuplicate(trimmedVal, role?.userRoleID);
    }, 3000);

    return () => clearTimeout(timerRef.current);
  }, [
    values.roleName,
    role,
    checkDuplicate
  ]);

  const getRoleNameError = useCallback(() => {
    if (touched.roleName && errors.roleName) {
      return errors.roleName;
    }

    if (isDuplicate) {
      return 'Role name already exists';
    }

    if (duplicateError) {
      return 'Error checking role name availability';
    }

    return '';
  }, [touched.roleName, errors.roleName, values.roleName, isDuplicate, duplicateError]);

  const isRoleNameError = Boolean(
    (touched.roleName && errors.roleName) || (values.roleName && isDuplicate) || (values.roleName && duplicateError)
  );

  const isSaveDisabled = Boolean(
    isSubmitting ||
    (values.roleName && isDuplicate) ||
    (values.roleName && duplicateError) ||
    !values.roleName ||
    (role && !hasFormChanges())
  );


  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {role ? (
              <>
                <Typography component="span" sx={{ color: 'primary.main' }}>
                  Edit
                </Typography>{' '}
                Role
              </>
            ) : (
              <>
                <Typography component="span" sx={{ color: 'primary.main' }}>
                  New
                </Typography>{' '}
                Role
              </>
            )}
          </Typography>
          <IconButton
            aria-label="close"
            //onClick={closeModal}
            onMouseDown={(e) => {
              e.preventDefault();
              isClosingRef.current = true;
              closeModal();
            }}
            sx={{
              color: (theme) => theme.palette.primary.main
            }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Stack sx={{ gap: 1, pt: 2 }}>
                <InputLabel htmlFor="role-name" required>Role Name</InputLabel>
                <TextField
                  autoFocus
                  required
                  fullWidth
                  id="role-name"
                  placeholder="Enter Role Name"
                  {...getFieldProps('roleName')}
                  onBlur={(e) => {
                    if (isClosingRef.current) return;
                    formik.handleBlur(e);
                    const trimmedVal = e.target.value.trim();
                    const originalVal = (role?.roleName || '').trim();

                    if (
                      trimmedVal &&
                      trimmedVal !== originalVal &&
                      trimmedVal !== lastCheckedNameRef.current
                    ) {
                      if (timerRef.current) clearTimeout(timerRef.current);

                      checkDuplicate(trimmedVal, role?.userRoleID);
                    }
                  }}
                  slotProps={{ input: { inputProps: { maxLength: 100 } } }}
                  helperText={getRoleNameError()}
                  error={isRoleNameError}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel htmlFor="role-description">Description</InputLabel>
                <TextField
                  required
                  fullWidth
                  id="role-description"
                  placeholder="Enter Role Description"
                  {...getFieldProps('roleDescription')}
                  multiline
                  minRows={3}
                  slotProps={{ input: { inputProps: { maxLength: 250 } } }}
                  error={Boolean(touched.roleDescription && errors.roleDescription)}
                  helperText={touched.roleDescription && errors.roleDescription}
                />
              </Stack>
            </Grid>
          </Grid>
          {role && (
            <FormControlLabel
              control={<Switch checked={values.active} onChange={(e) => setFieldValue('active', e.target.checked)} />}
              label={values.active ? 'Active' : 'Inactive'}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ width: 1 }}>
            <Grid>
              {role && role.updatedOn && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '9px' }}>
                  Edited by{' '}
                  <Typography component="span" sx={{ fontSize: '9px', fontStyle: 'italic', color: 'primary.main' }}>
                    {role?.updatedByUserName}
                  </Typography>{' '}
                  at{' '}
                  <Typography component="span" sx={{ fontSize: '9px', fontStyle: 'italic', color: 'primary.main' }}>
                    {GetFormattedTimeOnly(new Date(role?.updatedOn ?? ''))}
                  </Typography>{' '}
                  on{' '}
                  <Typography component="span" sx={{ fontSize: '9px', fontStyle: 'italic', color: 'primary.main' }}>
                    {GetFormattedDate(new Date(role?.updatedOn ?? ''))}
                  </Typography>
                </Typography>
              )}
            </Grid>
            <Grid>
              <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSaveDisabled || !!isDuplicate}
                  startIcon={
                    isSubmitting ? (
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                        <CircularProgress size={16} color="inherit" />
                      </motion.div>
                    ) : (
                      <SaveOutlined />
                    )
                  }
                >
                  Save
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </DialogActions>
      </Form>
    </FormikProvider>
  );
};
export default RoleForm;
